import { create } from "zustand";

import { axiosApiInstance } from "@/libs/axios.tsx";
import { API_ENDPOINTS } from "@/constants/api.ts";

// Types based on the API response structure
export interface CreditBalance {
  available_credits: number;
}

export interface CreditPurchase {
  id: string;
  credits: number;
  description: string;
  purchased_at: string;
  payment_id: string;
}

export interface CreditUsage {
  id: string;
  credits_used: string;
  description: string;
  used_at: string;
  type: string;
  creditable_data: {
    type: string;
    id: string;
  };
  metadata: {
    duration_minutes?: number;
    rate_per_minute?: number;
  };
}

export interface MonthlyStatistics {
  current_month: {
    period: string;
    credits_used: number;
    credits_purchased: number;
    lessons_count: number;
  };
  previous_month: {
    period: string;
    credits_used: number;
    credits_purchased: number;
    lessons_count: number;
  };
  usage_trend_percentage: number;
}

export interface ChartDataPoint {
  date: string;
  value: number;
}

export interface ChartsData {
  credits_used_per_day: ChartDataPoint[];
  credits_purchased_per_day: ChartDataPoint[];
  payments_count_per_day: ChartDataPoint[];
  payments_amount_per_day: ChartDataPoint[];
  status_distribution: any[];
}

export interface AverageRates {
  credits_used_per_day: number;
  credits_used_per_week: number;
  payments_per_day: number;
  payments_amount_cents_per_day: number;
}

export interface PaymentsSummary {
  total: {
    count: number;
    amount_cents: number;
  };
  completed: {
    count: number;
    amount_cents: number;
  };
  pending: {
    count: number;
    amount_cents: number;
  };
  failed: {
    count: number;
    amount_cents: number;
  };
}

export interface FinancesData {
  current_balance: CreditBalance;
  latest_purchases: CreditPurchase[];
  latest_usages: CreditUsage[];
  monthly_statistics: MonthlyStatistics;
  charts?: ChartsData;
  avg_rates?: AverageRates;
  payments?: {
    summary: PaymentsSummary;
    prioritized: any[];
  };
}

interface FinancesStore {
  data: FinancesData | null;
  isLoading: boolean;
  error: string | null;
  setData: (data: FinancesData | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearData: () => void;
  refetch: () => Promise<void>;
}

export const useFinancesStore = create<FinancesStore>((set, get) => ({
  data: null,
  isLoading: false,
  error: null,
  setData: (data) => set({ data }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  clearData: () => set({ data: null, error: null }),
  refetch: async () => {
    const { setData, setLoading, setError } = get();

    try {
      setLoading(true);
      setError(null);

      const { data } = await axiosApiInstance.get(API_ENDPOINTS.TUTOR.FINANCES.CREDITS_OVERVIEW);

      setData(data.data);
    } catch (error: any) {
      console.error("Failed to refetch finances data:", error);
      setError(error.response?.data?.message || error.message || "Failed to fetch finances data");
    } finally {
      setLoading(false);
    }
  },
}));
